import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@app/core/services/auth.service';
import { I18nService } from '@app/core/services/i18n.service';
import { TAXI_OFFICE_KEYS } from '@app/core/i18n/keys/taxi-office.keys';
import { TRANSIT_KEYS } from '@app/core/i18n/keys/transit.keys';
import { SupportedLanguage } from '@app/core/i18n/supported-languages';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  readonly taxiKeys = TAXI_OFFICE_KEYS;
  readonly transitKeys = TRANSIT_KEYS;
  readonly languages: readonly SupportedLanguage[];
  /** Display name + optional logo + portal subtitle for the active session. */
  public header: { title: string; logoUrl?: string; subtitle?: string } = { title: '' };
  @Output() menuToggle = new EventEmitter<void>();

  private authSub?: Subscription;
  private langSub?: Subscription;

  constructor(
    private readonly auth: AuthService,
    private readonly i18n: I18nService,
    private readonly translate: TranslateService
  ) {
    this.languages = i18n.supportedLanguages;
  }

  isLanguageActive(code: string): boolean {
    return this.i18n.getActiveLanguage() === code;
  }

  setLanguage(code: string): void {
    this.i18n.setUserLanguage(code);
  }

  ngOnInit(): void {
    this.refresh();
    this.authSub = this.auth.isLoggedIn.subscribe(() => {
      // A login/logout changes the active portal, so re-apply the document state (portal class,
      // dir) for the saved language.
      void this.i18n.syncWithActivePortal();
      this.refresh();
    });
    this.langSub = this.translate.onLangChange.subscribe(() => this.refresh());
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.langSub?.unsubscribe();
  }

  private refresh(): void {
    if (this.auth.isProviderSession()) {
      const transit = this.auth.isTransitOperatorSession();
      const keys = transit ? this.transitKeys.header : this.taxiKeys.header;
      const p = this.auth.getProviderContext();
      const title =
        (p?.['businessName'] as string) ||
        (p?.['name'] as string) ||
        (p?.['officeName'] as string) ||
        this.i18n.instant(keys.defaultTitle);
      this.header = {
        title,
        logoUrl: (p?.['logoUrl'] as string) || undefined,
        subtitle: this.i18n.instant(keys.subtitle),
      };
      return;
    }
    const store = this.auth.getStore() ?? this.tryLegacyStore();
    if (store) {
      this.header = {
        title: (store['name'] as string) || this.i18n.instant('common.store'),
        logoUrl: store['logoUrl'] as string | undefined,
      };
      return;
    }
    this.header = { title: this.i18n.instant('common.appName') };
  }

  private tryLegacyStore(): Record<string, unknown> | null {
    const raw = localStorage.getItem('store');
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
}
